import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DoctorProfileUseForm } from './use-doctor-profile-form';

/** Select value standing for "no priority" (the form stores `null`). */
const NO_PRIORITY_VALUE = 'none';
const PRIORITY_VALUES = ['1', '2', '3', '4'] as const;
const TRIGGER_CLASSNAME = 'w-[120px]';

/**
 * Substitute priority select (1 = highest, 4 = lowest, or none). Only
 * rendered for staff roles that see priorities.
 *
 * @param props.form - the unified profile form
 * @returns the form-bound select
 */
export const PriorityField = ({ form }: { form: DoctorProfileUseForm }) => {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name="priority"
      render={({ field }) => (
        <FormItem>
          <div className="flex flex-row items-center justify-between gap-2 space-y-0">
            <FormLabel>{t('edit-user-profile-form.priority.label')}</FormLabel>

            <Select
              onValueChange={(value) => {
                field.onChange(
                  value === NO_PRIORITY_VALUE ? null : Number(value),
                );
              }}
              value={
                field.value != null ? String(field.value) : NO_PRIORITY_VALUE
              }
            >
              <FormControl>
                <SelectTrigger className={TRIGGER_CLASSNAME}>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>

              <SelectContent side="top">
                <SelectItem value={NO_PRIORITY_VALUE}>
                  {t('edit-user-profile-form.priority.none')}
                </SelectItem>
                {PRIORITY_VALUES.map((value) => (
                  <SelectItem
                    key={value}
                    value={value}
                  >
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};
